if (!customElements.get('purchase-options')) {
  customElements.define(
    'purchase-options',
    class PurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.idInput = this.querySelector('input[name="id"]');
        this.sellingPlanInput = this.querySelector('.po__selling-plan-input');
        this.priceEl = this.querySelector('[data-po-price]');
        this.compareEl = this.querySelector('[data-po-compare]');
        this.detailsList = Array.from(this.querySelectorAll('[data-po-details]'));
        this.cards = Array.from(this.querySelectorAll('[data-po-card]'));
        this.tabs = Array.from(this.querySelectorAll('[data-po-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-po-panel]'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.switchTab(tab.dataset.poTab));
        });

        this.cards.forEach((card) => {
          const radio = card.querySelector('.po__radio');
          if (!radio) return;
          radio.addEventListener('change', () => {
            if (radio.checked) this.selectCard(card);
          });
        });

        this.initTooltips();

        const defaultTab = this.dataset.defaultTab || 'subscribe';
        const checked = this.cards.find((c) => {
          const r = c.querySelector('.po__radio');
          return r && r.checked;
        });

        if (checked) {
          this.selectCard(checked);
        } else {
          this.selectDefaultForTab(defaultTab);
        }
      }

      initTooltips() {
        this.tips = Array.from(this.querySelectorAll('[data-po-tip]'));
        if (!this.tips.length) return;

        this.tips.forEach((tip) => {
          // Position before reveal so hover/focus (desktop) and tap (mobile) all land correctly.
          tip.addEventListener('mouseenter', () => this.positionTooltip(tip));
          tip.addEventListener('focus', () => this.positionTooltip(tip));
          // Touch devices have no hover, so tap toggles the tooltip open/closed.
          tip.addEventListener('click', (event) => {
            event.preventDefault();
            const willOpen = !tip.classList.contains('is-open');
            this.closeTooltips();
            if (willOpen) {
              this.positionTooltip(tip);
              tip.classList.add('is-open');
              tip.setAttribute('aria-expanded', 'true');
            }
          });
          tip.addEventListener('mouseleave', () => {
            tip.classList.remove('is-open');
            tip.setAttribute('aria-expanded', 'false');
          });
        });

        this.handleTipOutside = (event) => {
          if (!event.target.closest('[data-po-tip]')) this.closeTooltips();
        };
        this.handleTipKey = (event) => {
          if (event.key === 'Escape') this.closeTooltips();
        };
        this.handleTipResize = () => {
          this.tips.forEach((tip) => {
            if (tip.classList.contains('is-open')) this.positionTooltip(tip);
          });
        };
        document.addEventListener('click', this.handleTipOutside);
        document.addEventListener('keydown', this.handleTipKey);
        window.addEventListener('resize', this.handleTipResize);
      }

      // Anchors the bubble to the icon but clamps it inside the panel so it never
      // overflows on narrow (mobile) screens; keeps the arrow pointing at the icon.
      positionTooltip(tip) {
        const bubble = tip.querySelector('.po__tip-bubble');
        if (!bubble) return;

        const container = tip.closest('.po__details-inner') || tip.closest('.po') || document.documentElement;
        const margin = 8;
        const containerRect = container.getBoundingClientRect();
        const available = Math.max(0, containerRect.width - margin * 2);
        bubble.style.maxWidth = Math.min(320, available) + 'px';

        const tipRect = tip.getBoundingClientRect();
        const bubbleWidth = bubble.offsetWidth;
        const iconCenter = tipRect.left + tipRect.width / 2;

        const minLeft = containerRect.left + margin;
        const maxLeft = containerRect.right - margin - bubbleWidth;
        let left = iconCenter - bubbleWidth / 2;
        if (left > maxLeft) left = maxLeft;
        if (left < minLeft) left = minLeft;

        bubble.style.setProperty('--po-tip-left', left - tipRect.left + 'px');

        let arrow = iconCenter - left;
        const arrowMin = 14;
        const arrowMax = bubbleWidth - 14;
        if (arrow < arrowMin) arrow = arrowMin;
        if (arrow > arrowMax) arrow = arrowMax;
        bubble.style.setProperty('--po-tip-arrow', arrow + 'px');
      }

      closeTooltips() {
        (this.tips || []).forEach((tip) => {
          tip.classList.remove('is-open');
          tip.setAttribute('aria-expanded', 'false');
        });
      }

      disconnectedCallback() {
        if (this.handleTipOutside) document.removeEventListener('click', this.handleTipOutside);
        if (this.handleTipKey) document.removeEventListener('keydown', this.handleTipKey);
        if (this.handleTipResize) window.removeEventListener('resize', this.handleTipResize);
      }

      switchTab(name) {
        this.tabs.forEach((t) => {
          t.setAttribute('aria-selected', t.dataset.poTab === name ? 'true' : 'false');
        });
        this.panels.forEach((p) => {
          p.hidden = p.dataset.poPanel !== name;
        });
        this.selectDefaultForTab(name);
      }

      selectDefaultForTab(name) {
        const inTab = this.cards.filter((c) => c.dataset.tab === name);
        if (!inTab.length) return;

        let target = inTab[0];
        if (name === 'subscribe') {
          const di = parseInt(this.dataset.defaultOption, 10);
          const match = inTab.find((c) => parseInt(c.dataset.index, 10) === di);
          if (match) target = match;
        }

        const radio = target.querySelector('.po__radio');
        if (radio) radio.checked = true;
        this.selectCard(target);
      }

      selectCard(card) {
        this.cards.forEach((c) => c.classList.toggle('is-selected', c === card));

        const variantId = card.dataset.variantId || '';
        if (this.idInput && variantId) {
          this.idInput.value = variantId;
        }

        const sellingPlan = card.dataset.sellingPlan || '';
        if (this.sellingPlanInput) {
          if (sellingPlan) {
            this.sellingPlanInput.name = 'selling_plan';
            this.sellingPlanInput.value = sellingPlan;
          } else {
            this.sellingPlanInput.removeAttribute('name');
            this.sellingPlanInput.value = '';
          }
        }

        if (this.priceEl) this.priceEl.textContent = card.dataset.price || '';
        if (this.compareEl) {
          const compare = card.dataset.compare;
          if (compare) {
            this.compareEl.textContent = compare;
            this.compareEl.hidden = false;
          } else {
            this.compareEl.textContent = '';
            this.compareEl.hidden = true;
          }
        }

        const index = card.dataset.index;
        this.detailsList.forEach((d) => {
          d.hidden = d.dataset.detailsIndex !== index;
        });
      }
    }
  );
}
